import React, { useContext, useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import UserContext from "../contexts/UserContext";
import { loginRequest } from "../authConfig";
import api from "../utils/api";
import { recordAuthDiag } from "../utils/authDiag";
import LoadingSpinner from "./ui/LoadingSpinner";
import { canAccess } from "../utils/roleHierarchy";

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const ACTIVITY_KEY = 'lastActivity';

function isSessionExpired() {
  const last = localStorage.getItem(ACTIVITY_KEY);
  if (!last) return true;
  return Date.now() - parseInt(last, 10) > SESSION_TIMEOUT_MS;
}

function touchActivity() {
  localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
}

function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userData');
  localStorage.removeItem(ACTIVITY_KEY);
}

const ProtectedRoute = ({ children, role, roles }) => {
  const allowed = roles ?? role;
  const { user, setUser } = useContext(UserContext);
  const { instance, accounts } = useMsal();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(!user && !!token);
  const [attemptedFetch, setAttemptedFetch] = useState(false);
  const [expired, setExpired] = useState(false);

  // Check session timeout on mount
  useEffect(() => {
    if (token && isSessionExpired()) {
      clearSession();
      instance.clearCache().catch(() => {});
      setUser(null);
      setExpired(true);
      setLoading(false);
    }
  }, [token, instance, setUser]);

  // Track user activity — reset timeout on interaction
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handler = () => touchActivity();
    // Touch on mount
    touchActivity();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handler));
  }, [user]);

  // Auto-logout check every 60 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      if (isSessionExpired()) {
        clearSession();
        instance.clearCache().catch(() => {});
        setUser(null);
        setExpired(true);
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [user, instance, setUser]);

  // Fetch user data with MSAL token refresh
  useEffect(() => {
    if (!token || user || attemptedFetch || expired) {
      setLoading(false);
      return;
    }

    setAttemptedFetch(true);

    const fetchUserData = async () => {
      try {
        const response = await api.get(`/api/users/details`);
        if (response.data) {
          setUser(response.data);
          touchActivity();
          setLoading(false);
          return;
        }
      } catch (error) {
        const account = accounts[0];
        if (account) {
          try {
            const tokenResponse = await instance.acquireTokenSilent({
              ...loginRequest,
              account,
            });
            if (tokenResponse?.accessToken) {
              localStorage.setItem('token', tokenResponse.accessToken);
              const retryRes = await api.get(`/api/users/details`);
              if (retryRes.data) {
                setUser(retryRes.data);
                touchActivity();
                setLoading(false);
                return;
              }
            }
          } catch (msalError) {
            // DIAGNOSTIC (persist for the intermittent double sign-in).
            recordAuthDiag('ProtectedRoute', msalError, {
              accounts: accounts.length,
              hadToken: !!token,
            });
            if (msalError instanceof InteractionRequiredAuthError) {
              // DIAGNOSTIC (intermittent double sign-in): a protected page couldn't
              // get the API token silently and is bouncing to Azure for a SECOND
              // interactive sign-in. If you see this, this is the extra prompt.
              console.warn('[auth][ProtectedRoute] acquireTokenSilent needs interaction → acquireTokenRedirect (SECOND sign-in)', msalError?.errorCode);
              instance.acquireTokenRedirect({ ...loginRequest, account });
              return;
            }
            console.warn('MSAL silent refresh failed:', msalError.message);
          }
        }
        clearSession();
        setUser(null);
      }
      setLoading(false);
    };

    fetchUserData();
  }, [token, user, setUser, attemptedFetch, instance, accounts, expired]);

  // Clear user context if no token
  useEffect(() => {
    if (!token && user) {
      setUser(null);
    }
  }, [token, user, setUser]);

  if (!token || expired) return <Navigate to="/login" replace />;

  if (loading) {
    return <LoadingSpinner variant="page" message="Loading..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Role check via hierarchy helper: superadmin ⊇ finance_ops,
  // demo_introducing_agent ⊆ introducing_agent, etc.
  if (allowed && !canAccess(user.role, allowed)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute;
